import React, { useEffect, useRef, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Organisation } from "../types";
import {
  checkSlugAvailability,
  checkTrialEligibility,
  createCheckoutSession,
  saveOrganisation,
} from "../services/storageService";
import { generateId } from "../services/utils";
import { AlertCircle } from "./Icons";
import { Modal } from "./Modals";

interface CreateOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrgCreated: (org: Organisation) => void;
}

export const CreateOrgModal: React.FC<CreateOrgModalProps> = ({
  isOpen,
  onClose,
  onOrgCreated,
}) => {
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSlug, setNewOrgSlug] = useState("");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();
  const { getToken } = useAuth();
  const debounceRef = useRef<null | NodeJS.Timeout>(null);

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedPlan, setSelectedPlan] = useState<"TEAM" | "ENTERPRISE">(
    "TEAM",
  );
  const [isTrialEligible, setIsTrialEligible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewOrgName("");
      setNewOrgSlug("");
      setSlugError(null);
      setStep(1);

      // Check trial eligibility
      getToken().then(async (token) => {
        try {
          const { eligible } = await checkTrialEligibility(token || undefined);
          setIsTrialEligible(eligible);
        } catch (e) {
          console.error("Failed to check trial eligibility", e);
        }
      });
    }
  }, [isOpen, getToken]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setNewOrgName(name);
    // Basic slugify
    const autoSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setNewOrgSlug(autoSlug);
    validateSlug(autoSlug);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setNewOrgSlug(slug);
    validateSlug(slug);
  };

  const validateSlug = (slug: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSlugError(null);

    if (!slug) return;
    if (slug.length < 3) {
      return;
    }

    setIsCheckingSlug(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = await getToken();
        const available = await checkSlugAvailability(slug, token || undefined);
        if (!available) {
          setSlugError("This URL is already taken.");
        }
      } catch (e) {
        console.error("Failed to check slug", e);
        setSlugError("Failed to validate URL availability");
      } finally {
        setIsCheckingSlug(false);
      }
    }, 500);
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgSlug.trim() || slugError || !user) return;

    setIsSubmitting(true);
    try {
      const token = await getToken();
      const newOrg: Organisation = {
        id: generateId(),
        name: newOrgName,
        slug: newOrgSlug,
        type: "SHARED",
        ownerId: user.id,
        memberIds: [user.id],
        role: "MAINTAINER",
        subscriptionStatus: "TRIALING",
      };
      // Note: Backend ignores subscriptionStatus in input but will set it to TRIALING
      const savedOrg = await saveOrganisation(
        { ...newOrg, planId: selectedPlan },
        token || undefined,
      );

      if (savedOrg.subscriptionStatus === "TRIAL_EXPIRED") {
        // Redirect to Stripe immediately
        try {
          const { url } = await createCheckoutSession(
            savedOrg.id,
            selectedPlan,
            token || undefined,
          );
          window.location.href = url;
          return; // Don't close modal or trigger onOrgCreated yet, user is redirecting
        } catch (e) {
          console.error("Failed to create checkout session", e);
          setSlugError(
            "Workspace created but failed to start payment session. Please go to billing.",
          );
          onOrgCreated(savedOrg); // Fallback
          onClose();
        }
      } else {
        onOrgCreated(savedOrg);
        onClose();
      }
    } catch (e: any) {
      setSlugError(e.message || "Failed to create workspace");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Shared Workspace">
      <div className="space-y-6">
        {step === 1 ? (
          <>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Create a shared space for your organisation to collaborate.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 uppercase">
                  Workspace Name
                </label>
                <input
                  autoFocus
                  className="w-full text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 px-3 py-2.5 text-slate-900 dark:text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                  placeholder="Acme Corp"
                  maxLength={100}
                  value={newOrgName}
                  onChange={handleNameChange}
                  data-testid="org-name-input"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 uppercase">
                  Workspace URL
                </label>
                <div
                  className={`flex items-center border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900 transition-colors ${slugError ? "border-red-500 ring-1 ring-red-500" : "border-slate-200 dark:border-slate-700 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500"}`}
                >
                  <span className="pl-3 pr-1 text-slate-400 text-sm whitespace-nowrap">
                    taskplan.run /
                  </span>
                  <input
                    className="flex-1 w-full text-sm bg-transparent pr-2 py-2.5 text-slate-900 dark:text-white outline-none placeholder-slate-400"
                    placeholder="acme-corp"
                    maxLength={50}
                    value={newOrgSlug}
                    onChange={handleSlugChange}
                    data-testid="org-slug-input"
                  />
                  {isCheckingSlug && (
                    <div className="pr-3">
                      <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                {slugError && (
                  <div className="flex items-center gap-1.5 mt-2 text-red-500 text-xs animate-in slide-in-from-top-1">
                    <AlertCircle size={12} />
                    <span>{slugError}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={
                  !newOrgName || !newOrgSlug || !!slugError || isCheckingSlug
                }
                data-testid="org-continue-button"
                className="px-4 py-2 bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg hover:bg-indigo-700 font-medium text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95"
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-4">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 p-4 rounded-xl">
                <p className="text-sm text-indigo-900 dark:text-indigo-200 font-medium">
                  Shared workspaces require a paid plan to support collaboration
                  and advanced features.
                </p>
                <p className="text-xs text-indigo-700/70 dark:text-indigo-300/70 mt-1">
                  {isTrialEligible ? (
                    <>
                      Both plans include a <strong>14-day free trial</strong>.
                      No credit card required to start.
                    </>
                  ) : (
                    <>
                      You have already used your free trial. You will be
                      redirected to payment after creation.
                    </>
                  )}
                </p>
              </div>
              <div className="grid gap-4">
                <button
                  onClick={() => setSelectedPlan("TEAM")}
                  data-testid="plan-team-button"
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left ${selectedPlan === "TEAM" ? "border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10" : "border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700"}`}
                >
                  <div
                    className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === "TEAM" ? "border-indigo-600" : "border-slate-300 dark:border-slate-600"}`}
                  >
                    {selectedPlan === "TEAM" && (
                      <div className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 dark:text-white">
                        Team Plan
                      </span>
                      <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full font-bold uppercase">
                        Popular
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Perfect for teams collaborating on multiple projects.{" "}
                      <a
                        href="/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Read more
                      </a>
                      .
                    </p>
                    <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 mt-2">
                      £25/mo{" "}
                      <span className="text-xs font-normal text-slate-400">
                        after trial
                      </span>
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedPlan("ENTERPRISE")}
                  data-testid="plan-enterprise-button"
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left ${selectedPlan === "ENTERPRISE" ? "border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10" : "border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700"}`}
                >
                  <div
                    className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === "ENTERPRISE" ? "border-indigo-600" : "border-slate-300 dark:border-slate-600"}`}
                  >
                    {selectedPlan === "ENTERPRISE" && (
                      <div className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
                    )}
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 dark:text-white">
                      Enterprise Plan
                    </span>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      For large organisations needing audit logs and advanced
                      security.{" "}
                      <a
                        href="/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Read more
                      </a>
                      .
                    </p>
                    <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 mt-2">
                      £150/mo{" "}
                      <span className="text-xs font-normal text-slate-400">
                        after trial
                      </span>
                    </p>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-between gap-3 pt-2">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
              >
                Back
              </button>
              <button
                onClick={handleCreateOrg}
                disabled={isSubmitting}
                data-testid="submit-org-button"
                className="px-6 py-2 bg-indigo-600 disabled:opacity-50 text-white rounded-lg hover:bg-indigo-700 font-bold text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95 flex items-center gap-2"
              >
                {isSubmitting
                  ? "Creating..."
                  : isTrialEligible
                    ? "Start 14-day free trial"
                    : "Complete Setup"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
